import React, { useRef, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaHome, FaImages, FaInfoCircle, FaEnvelope, FaCompactDisc, FaGlobe, FaCalendarAlt } from 'react-icons/fa';
import { useLanguage } from '../contexts/LanguageContext';
import './Navigation.css';

const Navigation = ({ selectedProject, onProjectChange }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { language, toggleLanguage, t } = useLanguage();
  const clickSoundRef = useRef(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const isTogglingRef = useRef(false); // Флаг для предотвращения множественных переключений

  // Инициализация звукового файла для кликов
  useEffect(() => {
    const clickSound = new Audio('/sound/click.mp3');
    clickSound.volume = 0.25; // Немного тише для навигации
    clickSound.preload = 'auto';
    clickSoundRef.current = clickSound;

    return () => {
      if (clickSound) {
        clickSound.pause();
        clickSound.src = '';
      }
    };
  }, []);

  // Функция для воспроизведения звука клика
  const playClickSound = () => {
    try {
      const soundFile = clickSoundRef.current;
      if (!soundFile) return;

      // Клонируем аудио элемент для одновременного воспроизведения
      const audioClone = soundFile.cloneNode();
      audioClone.volume = 0.25;
      audioClone.currentTime = 0;
      
      audioClone.play().catch(err => {
        // Игнорируем ошибки (например, если пользователь не взаимодействовал со страницей)
        console.debug('Navigation click sound error (ignored):', err);
      });
    } catch (error) {
      console.debug('Navigation sound error (ignored):', error);
    }
  };

  // Обработчик клика для навигации
  const handleNavClick = () => {
    playClickSound();
    setIsMobileMenuOpen(false); // Закрываем мобильное меню при клике на ссылку
  };

  // Обработчик клика на Gallery - сбрасываем состояние
  const handleGalleryClick = () => {
    playClickSound();
    setIsMobileMenuOpen(false);
    // Очищаем состояние выбранного альбома при переходе на Gallery
    localStorage.removeItem('gallery_selectedAlbum');
  };

  // Обработчик клика на логотип для обновления страницы
  const handleLogoClick = () => {
    playClickSound();
    setIsMobileMenuOpen(false);
    
    // Обновляем страницу, оставаясь на текущей странице
    window.location.reload();
  };

  // Закрываем мобильное меню при изменении маршрута
  useEffect(() => {
    if (isMobileMenuOpen) {
      setIsMobileMenuOpen(false);
    }
  }, [location.pathname]);

  // Закрываем мобильное меню при клике вне его (оптимизировано для мобильных)
  useEffect(() => {
    if (!isMobileMenuOpen) return;

    const handleClickOutside = (event) => {
      // Проверяем, что клик был вне меню и вне кнопки
      const target = event.target;
      const isClickInsideMenu = target.closest('.mobile-menu');
      const isClickOnButton = target.closest('.burger-menu-btn');
      
      if (!isClickInsideMenu && !isClickOnButton) {
        // Используем requestAnimationFrame для плавного закрытия
        requestAnimationFrame(() => {
          setIsMobileMenuOpen(false);
        });
      }
    };

    // Используем capture phase для более быстрой обработки
    document.addEventListener('mousedown', handleClickOutside, true);
    document.addEventListener('touchstart', handleClickOutside, true);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
      document.removeEventListener('touchstart', handleClickOutside, true);
    };
  }, [isMobileMenuOpen]);

  return (
    <motion.nav 
      className={`navigation lang-${language}`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="nav-container">
        <motion.div 
          className="logo"
          whileHover={{ scale: 1.1, rotate: 5 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleLogoClick}
          style={{ cursor: 'pointer' }}
        >
          <span>Sergey Levitskiy</span>
        </motion.div>

        {/* Десктопное меню */}
        <div className="desktop-menu">
          <div className="project-selector">
            <motion.button
              className={`project-btn ${location.pathname === '/commercial' ? 'active' : ''}`}
              onClick={() => {
                handleNavClick();
                onProjectChange('commercial');
                navigate('/commercial');
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Commercial
            </motion.button>
            <motion.button
              className={`project-btn ${location.pathname === '/underground' ? 'active' : ''}`}
              onClick={() => {
                handleNavClick();
                onProjectChange('underground');
                navigate('/underground');
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Underground
            </motion.button>
          </div>

          <div className="nav-links">
            <Link 
              to="/" 
              className={location.pathname === '/' ? 'active' : ''}
              onClick={handleNavClick}
            >
              <FaHome /> {t('nav.home')}
            </Link>
            <Link 
              to="/about" 
              className={location.pathname === '/about' ? 'active' : ''}
              onClick={handleNavClick}
            >
              <FaInfoCircle /> {t('nav.about')}
            </Link>
            <Link 
              to="/gallery" 
              className={location.pathname === '/gallery' ? 'active' : ''}
              onClick={handleGalleryClick}
            >
              <FaImages /> {t('nav.gallery')}
            </Link>
            <Link 
              to="/player" 
              className={location.pathname === '/player' ? 'active' : ''}
              onClick={handleNavClick}
            >
              <FaCompactDisc /> {t('nav.music')}
            </Link>
            <Link 
              to="/booking" 
              className={location.pathname === '/booking' ? 'active' : ''}
              onClick={handleNavClick}
            >
              <FaCalendarAlt /> {t('nav.booking')}
            </Link>
            <Link 
              to="/contacts" 
              className={location.pathname === '/contacts' ? 'active' : ''}
              onClick={handleNavClick}
            >
              <FaEnvelope /> {t('nav.contacts')}
            </Link>
            <motion.button
              className="language-toggle"
              onClick={() => {
                handleNavClick();
                toggleLanguage();
              }}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              title={language === 'ru' ? 'Switch to English' : 'Переключить на русский'}
            >
              <FaGlobe />
              <span>{language.toUpperCase()}</span>
            </motion.button>
          </div>
        </div>

        {/* Кнопка бургер-меню для мобильных устройств */}
        <motion.button
          className={`burger-menu-btn ${isMobileMenuOpen ? 'active' : ''}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            
            // Предотвращаем множественные переключения
            if (isTogglingRef.current) return;
            isTogglingRef.current = true;
            
            playClickSound();
            setIsMobileMenuOpen(prev => !prev);
            
            // Сбрасываем флаг после завершения анимации
            setTimeout(() => {
              isTogglingRef.current = false;
            }, 300);
          }}
          whileTap={{ scale: 0.95 }}
          aria-label="Toggle menu"
          aria-expanded={isMobileMenuOpen}
        >
          <div className="burger-icon">
            <motion.span
              className="burger-line"
              animate={isMobileMenuOpen ? { rotate: 45, y: 8 } : { rotate: 0, y: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
            />
            <motion.span
              className="burger-line"
              animate={isMobileMenuOpen ? { opacity: 0 } : { opacity: 1 }}
              transition={{ duration: 0.2 }}
            />
            <motion.span
              className="burger-line"
              animate={isMobileMenuOpen ? { rotate: -45, y: -8 } : { rotate: 0, y: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
            />
          </div>
        </motion.button>
      </div>

      {/* Мобильное меню */}
      <AnimatePresence mode="wait">
        {isMobileMenuOpen && (
          <motion.div
            className="mobile-menu"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ 
              duration: 0.25, 
              ease: [0.4, 0, 0.2, 1], // cubic-bezier для плавности
              opacity: { duration: 0.2 }
            }}
            style={{ willChange: 'opacity, transform' }}
          >
            <div className="mobile-menu-content">
              {/* Проекты */}
              <div className="mobile-project-selector">
                <motion.button
                  className={`mobile-project-btn ${location.pathname === '/commercial' ? 'active' : ''}`}
                  onClick={() => {
                    handleNavClick();
                    onProjectChange('commercial');
                    navigate('/commercial');
                  }}
                  whileTap={{ scale: 0.95 }}
                >
                  Commercial
                </motion.button>
                <motion.button
                  className={`mobile-project-btn ${location.pathname === '/underground' ? 'active' : ''}`}
                  onClick={() => {
                    handleNavClick();
                    onProjectChange('underground');
                    navigate('/underground');
                  }}
                  whileTap={{ scale: 0.95 }}
                >
                  Underground
                </motion.button>
              </div>

              {/* Навигационные ссылки */}
              <div className="mobile-nav-links">
                <div>
                  <Link 
                    to="/" 
                    className={location.pathname === '/' ? 'active' : ''}
                    onClick={handleNavClick}
                  >
                    <FaHome /> <span>{t('nav.home')}</span>
                  </Link>
                </div>
                <div>
                  <Link 
                    to="/about" 
                    className={location.pathname === '/about' ? 'active' : ''}
                    onClick={handleNavClick}
                  >
                    <FaInfoCircle /> <span>{t('nav.about')}</span>
                  </Link>
                </div>
                <div>
                  <Link 
                    to="/gallery" 
                    className={location.pathname === '/gallery' ? 'active' : ''}
                    onClick={handleGalleryClick}
                  >
                    <FaImages /> <span>{t('nav.gallery')}</span>
                  </Link>
                </div>
                <div>
                  <Link 
                    to="/player" 
                    className={location.pathname === '/player' ? 'active' : ''}
                    onClick={handleNavClick}
                  >
                    <FaCompactDisc /> <span>{t('nav.music')}</span>
                  </Link>
                </div>
                <div>
                  <Link 
                    to="/booking" 
                    className={location.pathname === '/booking' ? 'active' : ''}
                    onClick={handleNavClick}
                  >
                    <FaCalendarAlt /> <span>{t('nav.booking')}</span>
                  </Link>
                </div>
                <div>
                  <Link 
                    to="/contacts" 
                    className={location.pathname === '/contacts' ? 'active' : ''}
                    onClick={handleNavClick}
                  >
                    <FaEnvelope /> <span>{t('nav.contacts')}</span>
                  </Link>
                </div>
                <div>
                  <motion.button
                    className="mobile-language-toggle"
                    onClick={() => {
                      handleNavClick();
                      toggleLanguage();
                    }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <FaGlobe /> <span>{language.toUpperCase()}</span>
                    </motion.button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navigation;

